import classNames from "classnames"

import { Color } from "../../../constants/V2/color"
import PillButton from "../../elements/V2/Buttons/PillButton"
import TableSectionTitles from "../../elements/V2/TableSectionTitles"
import Typography from "../../elements/V2/Typography"
import { LayoutHeaderProps } from "../LayoutComponents/Header"
import LayoutSection from "../LayoutComponents/LayoutSection"
import {
  hasImageRow,
  extractColumnImages,
  extractRowHeadings,
  buildTableData,
} from "../Sections/ComparisonTable/utils"
import DoubleEntryComparisonTable from "../Sections/DoubleEntryComparisonTable"
import SingleEntryComparisonTable from "../Sections/SingleEntryComparisonTable"

import Accordion from "@components/elements/V2/Accordion"
import { getAccentColor } from "@utils/V2/color"
import React, { PropsWithChildren, useMemo } from "react"

type Props = PropsWithChildren<{
  sectionId: string
  rounded?: boolean
  backgroundColor: Color.Black | Color.Charcoal | Color.White | Color.MintGreen
  tableNumDesktopColumns: 3 | 4 | 6
  disclaimerText?: string
  buttonText?: string
  buttonBorderColor?: Color
  buttonLink?: string | YocoCom.YocoLink
  outerContainer?: "gridSection" | "tabSelector"
  id?: string
  tables?: Array<Storyblok.ComparisonTable>
  heading?: LayoutHeaderProps
}> &
  Partial<Storyblok.CTATrackingEvent>

const ComparisonTableSection = ({
  sectionId,
  rounded = true,
  backgroundColor,
  tableNumDesktopColumns,
  disclaimerText,
  buttonBorderColor,
  buttonText,
  buttonLink,
  outerContainer = "gridSection",
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  id,
  tables,
  heading,
  ...props
}: Props) => {
  const firstTable = tables?.[0]

  const showSliderHeading = useMemo(() => {
    if (!firstTable || firstTable.variant !== "singleEntry") return false

    const hasImages = hasImageRow(firstTable.data.thead)
    const data = buildTableData(
      firstTable.data.thead,
      firstTable.data.tbody,
      hasImages
    )
    const longestRow = data.reduce((max, row) => Math.max(max, row.length), 0)

    return longestRow > tableNumDesktopColumns
  }, [firstTable, tableNumDesktopColumns])

  return (
    <LayoutSection
      primaryBackgroundColor={backgroundColor}
      className="overflow-visible sm:overflow-clip"
      id={id}
      rounded={rounded}
      {...props}
    >
      {heading?.headingText && (
        <div
          className={classNames({
            "w-full sm:w-7/12": firstTable?.variant === "doubleEntry",
          })}
        >
          <TableSectionTitles
            sectionId={sectionId}
            showSliderHeading={showSliderHeading}
            heading={{
              ...heading,
              headingBodyColor:
                heading.headingBodyColor || getAccentColor(backgroundColor),
              headingTextColor:
                heading.headingTextColor || getAccentColor(backgroundColor),
              superscriptColor:
                heading.superscriptColor || getAccentColor(backgroundColor),
              alignment: heading.alignment || "left",
              mobileAlignment: heading.mobileAlignment || "left",
              headingGapSize: "small",
            }}
          />
        </div>
      )}

      {tables?.map((table, index) => {
        const hasImages = hasImageRow(table.data.thead)
        const columnImages = extractColumnImages(table.data.thead)
        const rowHeadings = extractRowHeadings(
          table.data.thead,
          table.data.tbody,
          hasImages
        )
        const tableData = buildTableData(
          table.data.thead,
          table.data.tbody,
          hasImages
        )

        return (
          <>
            {table.hasMobileAccordion && (
              <Accordion
                title={table.accordionTitle || ""}
                textColor={getAccentColor(backgroundColor)}
                iconColor={getAccentColor(backgroundColor)}
                openByDefault={index === 0}
                titleSize="body-lg-fixed"
                className="sm:hidden"
              >
                {table.variant === "singleEntry" ? (
                  <SingleEntryComparisonTable
                    key={`single-mobile-${index}`}
                    backgroundColor={backgroundColor}
                    rowHeadings={rowHeadings}
                    data={tableData}
                    outerContainer={outerContainer}
                    hideHeaders
                  />
                ) : (
                  <DoubleEntryComparisonTable
                    key={`double-mobile-${index}`}
                    backgroundColor={backgroundColor}
                    columnImages={columnImages}
                    rowHeadings={rowHeadings}
                    data={tableData}
                  />
                )}
              </Accordion>
            )}
            {table.hasDesktopHeader && table.accordionTitle && (
              <Typography
                font="grotesk"
                weight="book"
                color={getAccentColor(backgroundColor)}
                size="lead-sm"
                text={table.accordionTitle}
                className={classNames(
                  "hidden sm:block",
                  index > 0 ? "mt-40" : "mt-0"
                )}
              />
            )}
            {table.variant === "singleEntry" ? (
              <SingleEntryComparisonTable
                key={`single-desktop-${index}`}
                backgroundColor={backgroundColor}
                rowHeadings={rowHeadings}
                data={tableData}
                outerContainer={outerContainer}
                className={classNames({
                  "hidden sm:block": table.hasMobileAccordion,
                })}
              />
            ) : (
              <DoubleEntryComparisonTable
                key={`double-desktop-${index}`}
                backgroundColor={backgroundColor}
                columnImages={columnImages}
                rowHeadings={rowHeadings}
                data={tableData}
                className={classNames({
                  "hidden sm:block": table.hasMobileAccordion,
                })}
              />
            )}

            {index < tables.length - 1 && (
              <div className="my-8 h-[1px] w-full bg-gray/20 sm:mb-0 sm:mt-20" />
            )}
          </>
        )
      })}

      <div className="pt-lg grid grid-cols-1 items-center gap-20 sm:grid-cols-2">
        {disclaimerText && (
          <Typography
            text={disclaimerText}
            font="grotesk"
            weight="medium"
            size="body-lg"
            color={getAccentColor(backgroundColor)}
            className="col-span-1 col-start-1 h-fit w-fit"
          />
        )}
        {buttonText && buttonLink && buttonBorderColor && (
          <PillButton
            style="bordered"
            size="large"
            color={buttonBorderColor}
            text={buttonText}
            overrideTextColor={getAccentColor(backgroundColor)}
            linkUrl={buttonLink}
            className="col-span-1 col-start-1 w-fit sm:col-start-2 sm:justify-self-end"
            trackingEvent={trackingEvent}
            trackingEventKey={trackingEventKey}
            trackingEventValue={trackingEventValue}
          />
        )}
      </div>
    </LayoutSection>
  )
}

export default ComparisonTableSection
